import * as React from 'react';
import { FC, createContext, useCallback, useContext, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { IStore } from '@rbi-ctg/store';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { actions, useAppDispatch } from 'state/global-state';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLoyaltyContext } from 'state/loyalty';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { toast } from 'utils/toast';

import {
  IInRestaurantRedemptionCartEntry,
  InRestaurantRedemptionObject,
  isRedemptionObjectReward,
  isRewardCartEntry,
  useInRestaurantRedemptionCart,
} from './hooks/use-in-restaurant-redemption-cart';
import { ShortCodeState, useShortCode } from './hooks/use-short-code';
import { ShortCodePollingStatus, useShortCodePollStatus } from './hooks/use-short-code-poll-status';
import { ShortCodeConfirmationModal, ShortCodeOverrideModal } from './modals';
import { IInRestaurantRedemptionContext } from './types';
import { generateOrderLineItems } from './utils';

interface IOverrideModalConfirmation {
  overrideShortCodeCallback: () => void;
}
export const InRestaurantRedemptionContext = createContext<IInRestaurantRedemptionContext>(
  {} as IInRestaurantRedemptionContext
);
export const useInRestaurantRedemptionContext = () => useContext(InRestaurantRedemptionContext);

export const InRestaurantRedemptionProvider: FC = ({ children }) => {
  const { loyaltyUser, loyaltyLoading, refetchLoyaltyUser } = useLoyaltyContext();
  const { formatMessage } = useIntl();
  const { navigate } = useLocationContext();
  const { noStoreSelected, store, getStoreStatusFlags } = useStoreContext();
  const { isDelivery } = useServiceModeContext();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const previousLoyaltyUserIdRef = useRef<string>();
  const { ldUserIsAuthenticated, attemptGetUpdatedLdFlag } = useLDContext();
  const [ShortCodeOverrideModalConfirmation, openOverrideModalConfirmation] = useDialogModal<
    IOverrideModalConfirmation
  >({
    Component: ShortCodeOverrideModal,
    onConfirm: ({ overrideShortCodeCallback }: IOverrideModalConfirmation) => {
      overrideShortCodeCallback();
    },
  });

  const enableInRestaurantRedemption = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_IN_RESTAURANT_LOYALTY)
  );

  /** Evaluates InRestaurantRedemption LD flag against the current Store in context */
  const inRestaurantLoyaltyEnabledAtRestaurant = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_AT_THIS_RESTAURANT)
  );

  /** Evaluates InRestaurantRedemption feature availability against a store different from the one set in context */
  const isInRestaurantRedemptionEnabledAtStore = async (
    storeToCheck: IStore,
    serviceModeToCheck?: ServiceMode | null
  ) => {
    if (!isMobile) {
      return false;
    }

    const { isStoreOpenAndAvailable } = getStoreStatusFlags(storeToCheck, serviceModeToCheck);
    if (!isStoreOpenAndAvailable) {
      return false;
    }

    // Check LD flag without setting the Store on the LD context
    const inRestaurantRedemptionFlag = await attemptGetUpdatedLdFlag(
      LaunchDarklyFlag.ENABLE_LOYALTY_AT_THIS_RESTAURANT,
      {
        custom: {
          storeNumber: storeToCheck.number,
        },
      }
    );
    return Boolean(inRestaurantRedemptionFlag);
  };

  const inRestaurantRedemptionStatusPollIntervalMs = useFlag(
    LaunchDarklyFlag.LOYALTY_IN_RESTAURANT_STATUS_POLL_INTERVAL_MS
  );
  const dispatch = useAppDispatch();

  const loyaltyUserId = loyaltyUser?.id;

  const {
    inRestaurantRedemptionCart,
    addInRestaurantRedemptionEntry: addInRestaurantRedemption,
    removeInRestaurantRedemptionEntry: removeInRestaurantRedemption,
    updateInRestaurantRedemptionEntryQuantity: updateInRestaurantRedemptionQuantity,
    findInRestaurantCartEntryByIncentive,
    resetInRestaurantRedemptionCart,
    isInRestaurantRedemptionCartEmpty,
    existEntryTypeInRestaurantRedemptionCart,
    clearInRestaurantRedemptionAllRewards,
  } = useInRestaurantRedemptionCart();
  const {
    resetShortCode,
    getNewShortCode,
    shortCodeLoading,
    shortCodeState,
    setShortCodeClaimed,
    shortCode,
  } = useShortCode();

  const skipPolling: boolean =
    !inRestaurantRedemptionStatusPollIntervalMs ||
    shortCodeLoading ||
    shortCodeState !== ShortCodeState.Pending;

  const { pollingShortCodeStatus, resetPollingStatus } = useShortCodePollStatus({
    loyaltyId: loyaltyUserId,
    skipPolling,
    shortCode,
    pollInterval: inRestaurantRedemptionStatusPollIntervalMs,
  });

  const inRestaurantRedemptionEnabled = Boolean(
    isMobile && enableInRestaurantRedemption && !isDelivery && (!!loyaltyUserId || loyaltyLoading)
  );
  const storeNumber = store?.number;

  const resetInRestaurantRedemption = useCallback(() => {
    // reset all the in-restaurant state
    resetPollingStatus();
    resetShortCode();
    resetInRestaurantRedemptionCart();

    // reset loyalty state
    removeAppliedRewardsInStorage();
    dispatch(actions.loyalty.resetLoyaltyRewardState(loyaltyUser?.points ?? 0));
  }, [dispatch, loyaltyUser, resetInRestaurantRedemptionCart, resetPollingStatus, resetShortCode]);

  const generateShortCode = () => {
    if (loyaltyUserId && !noStoreSelected) {
      getNewShortCode({
        inRestaurantOrder: generateOrderLineItems(inRestaurantRedemptionCart),
        loyaltyId: loyaltyUserId,
        restaurantId: storeNumber,
      });
    }
  };

  useEffectOnUpdates(() => {
    resetShortCode();
    // generate a new short code on every cart update
    generateShortCode();
  }, [inRestaurantRedemptionCart]);

  useEffect(() => {
    if (pollingShortCodeStatus === ShortCodePollingStatus.Confirmed) {
      setShortCodeClaimed();
    }
  }, [pollingShortCodeStatus, setShortCodeClaimed]);

  useEffect(() => {
    previousLoyaltyUserIdRef.current = loyaltyUserId;
  }, [loyaltyUserId]);

  useEffect(() => {
    // clean cart if the user changes or the feature isn't enabled
    if (
      !loyaltyLoading &&
      !isInRestaurantRedemptionCartEmpty &&
      (previousLoyaltyUserIdRef.current !== loyaltyUserId ||
        !inRestaurantRedemptionEnabled ||
        (!inRestaurantLoyaltyEnabledAtRestaurant && ldUserIsAuthenticated))
    ) {
      resetInRestaurantRedemption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loyaltyLoading,
    loyaltyUserId,
    inRestaurantRedemptionEnabled,
    inRestaurantLoyaltyEnabledAtRestaurant,
  ]);

  /**
   * Adds the redemption object to the cart and applies the reward if necessary,
   * and displays a toast if the action was successful, and redirects the user
   * to the in-restaurant redemption page.
   */
  const addInRestaurantRedemptionEntry = useCallback(
    (redemptionObject: InRestaurantRedemptionObject) => {
      const isSuccess = addInRestaurantRedemption(redemptionObject);

      if (isSuccess) {
        let entityType = 'offer';

        if (isRedemptionObjectReward(redemptionObject)) {
          entityType = 'reward';
          // apply the reward
          const { engineReward } = redemptionObject;
          dispatch(
            actions.loyalty.applyReward({
              cartId: engineReward.id,
              rewardBenefitId: engineReward.rewardBenefitId,
            })
          );
        }

        toast.success(
          formatMessage(
            { id: 'addToInRestaurantRedemptionCartSuccess' },
            {
              type: formatMessage({
                id: entityType,
              }),
            }
          )
        );
      }
      // TODO: move this to the consumer
      navigate(formatMessage({ id: 'routes.redemptionInRestaurant' }));
    },
    [addInRestaurantRedemption, dispatch, formatMessage, navigate]
  );

  /**
   * Removes the cart entry from the cart and remove the reward from `applied rewards` if necessary.
   */
  const removeInRestaurantRedemptionEntry = useCallback(
    (cartEntry: IInRestaurantRedemptionCartEntry) => {
      removeInRestaurantRedemption(cartEntry);

      if (isRewardCartEntry(cartEntry)) {
        const { engineReward } = cartEntry.details;

        // remove the applied reward
        dispatch(
          actions.loyalty.removeAppliedReward({
            cartId: engineReward.id,
            rewardBenefitId: engineReward.rewardBenefitId,
          })
        );
      }
    },
    [dispatch, removeInRestaurantRedemption]
  );

  /**
   * Updates the cart entry quantity and apply or unapply the reward depending on the given and previous quantity.
   */
  const updateInRestaurantRedemptionEntryQuantity = useCallback(
    (cartEntry: IInRestaurantRedemptionCartEntry, quantity: number) => {
      updateInRestaurantRedemptionQuantity(cartEntry, quantity);

      // apply or un-apply the reward as many times as necessary
      if (isRewardCartEntry(cartEntry)) {
        const {
          details: { engineReward },
          quantity: beforeQuantity,
        } = cartEntry;
        const { applyReward, unApplyReward } = actions.loyalty;
        const isDecrementAction = quantity - beforeQuantity < 0;
        const handleAction = isDecrementAction ? unApplyReward : applyReward;

        const { id: cartId, rewardBenefitId } = engineReward;
        for (let i = 0; i < Math.abs(quantity - beforeQuantity); i++) {
          dispatch(
            handleAction({
              rewardBenefitId,
              cartId,
              loyaltyUser,
            })
          );
        }
      }
    },
    [dispatch, loyaltyUser, updateInRestaurantRedemptionQuantity]
  );

  /**
   * The actions that have the ability to override the short code, should be wrapper by
   * this function in order to display a confirmation modal before the action is called.
   */
  const overrideShortCodeActionWrapper = useCallback(
    <T extends (...args: any) => void>(callback: T): ((...args: Parameters<T>) => void) => (
      ...args: Parameters<T>
    ) => {
      if (pollingShortCodeStatus === ShortCodePollingStatus.Processing) {
        // call to the callback to override the state
        openOverrideModalConfirmation({
          overrideShortCodeCallback: () => callback(...args),
        });
      } else {
        callback(...args);
      }
    },
    [openOverrideModalConfirmation, pollingShortCodeStatus]
  );

  return (
    <InRestaurantRedemptionContext.Provider
      value={{
        inRestaurantLoyaltyEnabledAtRestaurant,
        generateShortCode,
        inRestaurantRedemptionCart,
        // applying the override short code wrapper to catch up when the cart is changing
        // and display a confirmation modal
        addInRestaurantRedemptionEntry: overrideShortCodeActionWrapper<
          typeof addInRestaurantRedemptionEntry
        >(addInRestaurantRedemptionEntry),
        removeInRestaurantRedemptionEntry: overrideShortCodeActionWrapper<
          typeof removeInRestaurantRedemptionEntry
        >(removeInRestaurantRedemptionEntry),
        updateInRestaurantRedemptionEntryQuantity: overrideShortCodeActionWrapper<
          typeof updateInRestaurantRedemptionEntryQuantity
        >(updateInRestaurantRedemptionEntryQuantity),
        findInRestaurantCartEntryByIncentive,
        isInRestaurantRedemptionCartEmpty,
        inRestaurantRedemptionEnabled,
        resetInRestaurantRedemption,
        shortCodeLoading,
        shortCodeState,
        shortCode,
        existEntryTypeInRestaurantRedemptionCart,
        clearInRestaurantRedemptionAllRewards,
        isInRestaurantRedemptionEnabledAtStore,
      }}
    >
      {shortCodeState === ShortCodeState.Claimed && (
        <ShortCodeConfirmationModal
          onAction={() => {
            resetInRestaurantRedemption();

            // re-fetching user points
            refetchLoyaltyUser();
          }}
        />
      )}
      <ShortCodeOverrideModalConfirmation />

      {children}
    </InRestaurantRedemptionContext.Provider>
  );
};
